import { S3Client, PutObjectCommand, GetObjectCommand } from '@aws-sdk/client-s3';

const bucketName = 'be-raw-data';

/**
 * Service class for handling S3 operations.
 */
export class S3Service {
  private s3Client: S3Client;

  // eslint-disable-next-line jsdoc/require-jsdoc
  constructor() {
    this.s3Client = new S3Client({
      region: 'us-east-2',
      credentials: {
        accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID || '',
        secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY || ''
      }
    });
  }

  /**
   * Uploads a file to S3 bucket.
   * @param file - File to upload.
   * @param key - Key of the file in S3.
   * @returns Promise resolving to upload result.
   */
  async uploadFile(file: File, key: string): Promise<any> {
    try {
      // Convert File object to ArrayBuffer
      const arrayBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      const params = {
        Bucket: bucketName,
        Key: key,
        Body: uint8Array,
        ContentType: file.type
      };

      const command = new PutObjectCommand(params);
      const response = await this.s3Client.send(command);
      return response;
    } catch (error) {
      console.error('Error uploading file to S3:', error);
      throw error;
    }
  }

  /**
   * Downloads a file from S3 bucket by its key.
   * @param key - Key of the file in S3.
   * @returns Promise resolving to the file data.
   */
  async downloadFile(key: string): Promise<Blob> {
    try {
      const params = {
        Bucket: bucketName,
        Key: key
      };

      const command = new GetObjectCommand(params);
      const response = await this.s3Client.send(command);

      if (!response.Body) {
        throw new Error('No file content received');
      }

      // Convert readable stream to blob.
      // Cast required since TS 5.7+: transformToByteArray() yields a
      // Uint8Array<ArrayBufferLike>, which BlobPart no longer accepts directly.
      const responseArrayBuffer = await response.Body.transformToByteArray();
      return new Blob([responseArrayBuffer as BlobPart], { type: response.ContentType });
    } catch (error) {
      console.error('Error downloading file from S3:', error);
      throw error;
    }
  }
}

// Create singleton instance
export const s3Service = new S3Service();
